// Tracking tags load lazily and only after cookie consent (SiteAnalytics.tsx), but the order
// success page fires its purchase on hydration — before the Google tag's `config` commands or the
// Meta Pixel's `fbq` exist. gtag.js routes an `event` only to destinations configured before it,
// and the success page used to skip fbq outright when it wasn't defined yet, so both purchases
// were lost on a normal visit. Callbacks queued here run once the tag's init script (ending with
// trackerReadySnippet) marks it ready, or right away if it already has. A tag that never loads
// (not configured, no consent) never runs its callbacks.

export type TrackerName = 'gtag' | 'fbq';

export interface GtagTargets {
    ga4Id: string | null;
    // "AW-XXXXXXXXX" — the Ads account tag (remarketing events go here).
    adsId: string | null;
    // "AW-XXXXXXXXX/label" — the Ads purchase conversion action to send conversions to.
    adsConversion: string | null;
    // Merchant Center account ID — enables Ads "conversions with cart data".
    merchantId: string | null;
}

type TrackerRegistry = {
    ready: Partial<Record<TrackerName, unknown>>;
    queue: Partial<Record<TrackerName, Array<(info: any) => void>>>;
};

declare global {
    interface Window {
        __stagTrackers?: TrackerRegistry;
    }
}

export function onTrackerReady(name: 'gtag', callback: (targets: GtagTargets) => void): void;
export function onTrackerReady(name: 'fbq', callback: () => void): void;
export function onTrackerReady(name: TrackerName, callback: (info: any) => void): void {
    if (typeof window === 'undefined') return;
    const registry = (window.__stagTrackers ||= { ready: {}, queue: {} });
    if (name in registry.ready) {
        callback(registry.ready[name]);
        return;
    }
    (registry.queue[name] ||= []).push(callback);
}

/** A value as a JS literal safe to embed in an inline <script> (no `</script>` breakout). */
export function jsLiteral(value: unknown): string {
    return JSON.stringify(value ?? null).replace(/</g, '\\u003c');
}

/** Inline-script tail that marks `name` ready with `info` and runs everything queued for it. */
export function trackerReadySnippet(name: TrackerName, info: unknown = true): string {
    const key = jsLiteral(name);
    return `(function(r){r.ready[${key}]=${jsLiteral(info)};(r.queue[${key}]||[]).splice(0).forEach(function(cb){try{cb(r.ready[${key}])}catch(e){console.error(e)}})})(window.__stagTrackers=window.__stagTrackers||{ready:{},queue:{}});`;
}
